'use client'
import HotelListItem from '@/app/components/HotelListItem'
import HotelListItemClick from '@/app/components/HotelListItemClick'
import HotelsLoadingAnimation from '@/app/components/HotelsLoadingAnimation'
import RecommendationsEventHandler from '@/app/components/RecommendationsEventHandler'
import { SM_BREAKPOINT, UUID_NAMESPACE } from '@/app/constants'
import { DestPagesProps, HotelsListResponse } from '@/app/types'
import {
	FetchNextPageOptions,
	InfiniteData,
	InfiniteQueryObserverResult,
} from '@tanstack/react-query'
import { useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { debounce } from 'lodash'
import { useTranslations } from 'next-intl'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { v5 as uuid } from 'uuid'
import HotelListPlaceholder from './HotelsListPlaceholder'
import Button from './common/Button'

export interface HotelsListProps {
	lang?: string
	data: HotelsListResponse
	pageParams?: DestPagesProps
	showLoadMore?: boolean
	allData?: InfiniteData<HotelsListResponse>
	hasNextPage: boolean
	isFetchingNextPage: boolean
	shouldLoadMore: boolean
	totalPages: number
	isSingleHotelRequest?: boolean
	isLandmark?: boolean
	isFallbackData?: boolean
	isSimilarData?: boolean
	isComplete: boolean
	fetchNextPage: (
		options?: FetchNextPageOptions | undefined
	) => Promise<
		InfiniteQueryObserverResult<
			InfiniteData<HotelsListResponse, unknown>,
			Error
		>
	>
}

const HotelsList: FC<HotelsListProps> = (props) => {
	const {
		lang,
		data,
		pageParams,
		showLoadMore,
		allData,
		hasNextPage,
		shouldLoadMore,
		isFetchingNextPage,
		totalPages,
		isSingleHotelRequest,
		isLandmark,
		isComplete,
		isFallbackData,
		isSimilarData,
		fetchNextPage,
	} = props
	const t = useTranslations('Keywords')
	const hotels = allData
		? allData.pages.flatMap((page) => page.hotels)
		: data.hotels

	const [page, setPage] = useState(+(pageParams?.searchParams.page || 1))
	const isMobile = useMediaQuery(SM_BREAKPOINT)

	const loadMoreHotels = useCallback(async () => {
		if (
			isFetchingNextPage ||
			!hasNextPage ||
			!shouldLoadMore ||
			page >= totalPages
		)
			return

		await fetchNextPage()
		setPage((prevPage) => prevPage + 1)
	}, [
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		shouldLoadMore,
		page,
		totalPages,
	])

	const debouncedLoadMoreHotels = useRef(debounce(loadMoreHotels, 300)).current

	useEffect(() => {
		const handleScroll = () => {
			const scrollOffset = 750
			const shouldTriggerInfiniteScroll =
				shouldLoadMore &&
				page < Math.min(3, totalPages) &&
				window.innerHeight + document.documentElement.scrollTop >=
					document.documentElement.offsetHeight - scrollOffset &&
				hasNextPage &&
				!isFetchingNextPage &&
				page < totalPages

			if (shouldTriggerInfiniteScroll) {
				debouncedLoadMoreHotels()
			}
		}

		if (shouldLoadMore) {
			window.addEventListener('scroll', handleScroll)
			return () => {
				window.removeEventListener('scroll', handleScroll)
				debouncedLoadMoreHotels.cancel()
			}
		}
	}, [
		page,
		debouncedLoadMoreHotels,
		hasNextPage,
		isFetchingNextPage,
		shouldLoadMore,
		totalPages,
	])

	const shouldShowLoadMoreButton =
		showLoadMore &&
		shouldLoadMore &&
		hasNextPage &&
		!isFetchingNextPage &&
		page >= 3 &&
		page < totalPages

	return (
		<div
			className={classNames('hotels-list', {
				'single-hotel-list-card': isSingleHotelRequest && isMobile,
			})}
		>
			{isFallbackData || isSimilarData ? (
				<div
					className={classNames(
						'overflow-hidden transition-all h-0 delay-150 duration-300',
						{
							'h-[110px] mt-6': !isComplete,
						}
					)}
				>
					<HotelsLoadingAnimation />
				</div>
			) : null}
			<RecommendationsEventHandler hotels={hotels} complete={data.complete} />
			{hotels.map((item, index) => (
				<HotelListItem
					key={item.title + index}
					item={item}
					index={index}
					lang={lang}
					searchParams={data.searchParams}
					isSingleHotel={isSingleHotelRequest}
					isLandmark={isLandmark}
				>
					<HotelListItemClick
						passthruLink={`/${lang}/passthru?uid=${uuid(
							item.link,
							UUID_NAMESPACE
						)}`}
						link={item.link}
						provider={item.provider}
						item={item}
						searchParams={data.searchParams}
						price={item.rawPrice}
					/>
				</HotelListItem>
			))}
			{shouldShowLoadMoreButton ? (
				<Button
					name="loadmore"
					textColor="tertiary-950"
					fontSize="xl"
					bgColor="tertiary-950/10"
					onClick={loadMoreHotels}
					bold
					className="!rounded-full w-full h-14 mt-8"
				>
					{t('showMoreResults')}
				</Button>
			) : null}
			{isFetchingNextPage ? <HotelListPlaceholder showCount={false} /> : null}
		</div>
	)
}

export default HotelsList
